/**
 * Consent, suppression & the "why was this refused" drill-down —
 * SKNOTIFICATION-GLOBAL-CR-001 UICC-001 §6. Without the consent/suppression
 * forms here, a promotional send is permanently blocked: the gate checks
 * for a granted-consent row, but nothing could ever create one.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, TextInput, StyleSheet } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { useResponsive } from '../../lib/responsive';
import { useToast } from '../../components/toast';
import { PageHeader, EmptyState, Btn, Banner, SectionLabel } from '../../components/notif/ui';
import { Select } from '../../components/notif/select';
import { useSession } from '../../context/sessionContext';
import {
  recordConsent,
  recordSuppression,
  listSuppressions,
  upsertDndScrub,
  getSendDecisions,
} from '../../services/notification/governance';
import { handleApiError } from '../../utils/errorHandler';
import { relTime } from '../../utils/formatters';
import { GOVERNANCE_CHANNELS, MESSAGE_CLASSES, SUPPRESSION_KINDS } from '../../schema/governance';
import type { BackendSuppressionEntry, BackendSendDecision, MessageClassCode, SuppressionKind } from '../../schema/governance';
import type { ChannelType } from '../../schema/notification';

const ADMIN_ROLES = new Set(['org_admin', 'admin', 'platform_admin']);

function RecipientFields({ phone, setPhone, email, setEmail, colors }: {
  phone: string; setPhone: (v: string) => void; email: string; setEmail: (v: string) => void; colors: any;
}) {
  const inputStyle = [st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }];
  return (
    <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
      <View style={{ flex: 1, minWidth: 160 }}>
        <Text style={[st.label, { color: colors.ink3 }]}>Phone (E.164)</Text>
        <TextInput value={phone} onChangeText={setPhone} placeholder="+919876543210" placeholderTextColor={colors.ink4} style={inputStyle} />
      </View>
      <View style={{ flex: 1, minWidth: 160 }}>
        <Text style={[st.label, { color: colors.ink3 }]}>Email</Text>
        <TextInput value={email} onChangeText={setEmail} placeholder="recipient@example.com" placeholderTextColor={colors.ink4} autoCapitalize="none" style={inputStyle} />
      </View>
    </View>
  );
}

export default function ConsentSuppressionScreen() {
  const { theme: { colors } } = useTheme();
  const { pad } = useResponsive();

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: pad, gap: 20 }}>
      <PageHeader title="Consent & Suppression" subtitle="What the send-time gate actually checks before a promotional message goes out." />
      <ConsentCard colors={colors} />
      <SuppressionCard colors={colors} />
      <SendDecisionLookup colors={colors} />
      <DndScrubCard colors={colors} />
    </ScrollView>
  );
}

function ConsentCard({ colors }: { colors: any }) {
  const toast = useToast();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [channelKind, setChannelKind] = useState<ChannelType>('whatsapp');
  const [messageClassCode, setMessageClassCode] = useState<MessageClassCode>('promotional');
  const [consentState, setConsentState] = useState<'granted' | 'withdrawn'>('granted');
  const [saving, setSaving] = useState(false);

  const canSave = (!!phone.trim() || !!email.trim()) && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await recordConsent({
        recipient: { phone: phone.trim() || undefined, email: email.trim() || undefined },
        channelKind,
        messageClassCode,
        consentState,
      });
      toast.success('Consent recorded', { message: `${consentState} for ${channelKind}/${messageClassCode}` });
      setPhone(''); setEmail('');
    } catch (e) {
      handleApiError(e, 'Could not record consent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Consent ledger</SectionLabel>
      <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 12 }}>
        Only classes that require consent (promotional) ever check this. Latest row per recipient+channel+class wins.
      </Text>
      <RecipientFields phone={phone} setPhone={setPhone} email={email} setEmail={setEmail} colors={colors} />
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
        <View style={{ width: 140 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Channel</Text>
          <Select value={channelKind} onChange={setChannelKind} options={GOVERNANCE_CHANNELS} />
        </View>
        <View style={{ width: 150 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Message class</Text>
          <Select value={messageClassCode} onChange={setMessageClassCode} options={MESSAGE_CLASSES} />
        </View>
        <View style={{ width: 140 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>State</Text>
          <Select value={consentState} onChange={setConsentState} options={[{ value: 'granted', label: 'Granted' }, { value: 'withdrawn', label: 'Withdrawn' }]} />
        </View>
        <Btn label={saving ? 'Saving…' : 'Record consent'} kind="primary" size="sm" disabled={!canSave} onPress={save} />
      </View>
    </View>
  );
}

function SuppressionCard({ colors }: { colors: any }) {
  const toast = useToast();
  const [entries, setEntries] = useState<BackendSuppressionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [channelKind, setChannelKind] = useState<ChannelType>('whatsapp');
  const [suppressionKind, setSuppressionKind] = useState<SuppressionKind>('opt_out');
  const [hardOptOut, setHardOptOut] = useState(true);
  const [messageClassCode, setMessageClassCode] = useState<MessageClassCode>('promotional');
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); listSuppressions().then(setEntries).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const canSave = (!!phone.trim() || !!email.trim()) && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await recordSuppression({
        recipient: { phone: phone.trim() || undefined, email: email.trim() || undefined },
        channelKind,
        messageClassCode: hardOptOut ? undefined : messageClassCode,
        suppressionKind,
      });
      toast.success('Suppression recorded');
      setPhone(''); setEmail('');
      load();
    } catch (e) {
      handleApiError(e, 'Could not record suppression');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Suppression list</SectionLabel>
      <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 12 }}>
        Opt-out / bounce / complaint / DND-registry, at contact + purpose. A hard opt-out suppresses every class on the channel.
      </Text>

      {!loading && entries.length > 0 && (
        <View style={{ marginBottom: 14 }}>
          {entries.slice(0, 20).map((e) => (
            <View key={e.suppressionId} style={[st.tr, { borderBottomColor: colors.line }]}>
              <Text style={{ color: colors.ink2, fontSize: 12, width: 90, textTransform: 'capitalize' }}>{e.channelKind}</Text>
              <Text style={{ color: colors.ink3, fontSize: 12, width: 100 }}>{e.suppressionKind}</Text>
              <Text style={{ color: colors.ink4, fontSize: 12, width: 100 }}>{e.messageClassCode ?? 'all classes'}</Text>
              <Text style={{ color: colors.ink4, fontSize: 11, flex: 1 }}>{relTime(e.suppressedAt)}</Text>
            </View>
          ))}
        </View>
      )}
      {!loading && entries.length === 0 && <Text style={{ color: colors.ink4, fontSize: 12, marginBottom: 12 }}>No suppressions recorded.</Text>}

      <RecipientFields phone={phone} setPhone={setPhone} email={email} setEmail={setEmail} colors={colors} />
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
        <View style={{ width: 140 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Channel</Text>
          <Select value={channelKind} onChange={setChannelKind} options={GOVERNANCE_CHANNELS} />
        </View>
        <View style={{ width: 150 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Kind</Text>
          <Select value={suppressionKind} onChange={setSuppressionKind} options={SUPPRESSION_KINDS} />
        </View>
        <View style={{ width: 160 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Scope</Text>
          <Select
            value={hardOptOut ? 'all' : 'class'}
            onChange={(v) => setHardOptOut(v === 'all')}
            options={[{ value: 'all', label: 'All classes' }, { value: 'class', label: 'One class' }]}
          />
        </View>
        {!hardOptOut && (
          <View style={{ width: 150 }}>
            <Text style={[st.label, { color: colors.ink3 }]}>Message class</Text>
            <Select value={messageClassCode} onChange={setMessageClassCode} options={MESSAGE_CLASSES} />
          </View>
        )}
        <Btn label={saving ? 'Saving…' : 'Record'} kind="primary" size="sm" disabled={!canSave} onPress={save} />
      </View>
    </View>
  );
}

function SendDecisionLookup({ colors }: { colors: any }) {
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [decisions, setDecisions] = useState<BackendSendDecision[] | null>(null);
  const [loading, setLoading] = useState(false);

  const canSearch = (!!phone.trim() || !!email.trim()) && !loading;

  const search = async () => {
    if (!canSearch) return;
    setLoading(true);
    try {
      const rows = await getSendDecisions({ phone: phone.trim() || undefined, email: email.trim() || undefined });
      setDecisions(rows);
    } catch (e) {
      handleApiError(e, 'Could not look up send decisions');
    } finally {
      setLoading(false);
    }
  };

  const decisionColor: Record<string, string> = { allow: '#2D8A5F', refuse: '#C94444', deferred: '#B87D0A' };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Why was this refused?</SectionLabel>
      <Text style={{ color: colors.ink4, fontSize: 11, marginBottom: 12 }}>
        Every send-time gate evaluation, by recipient. There's no reference-id lookup — the underlying table only
        keys by recipient, not the original notify() call.
      </Text>
      <RecipientFields phone={phone} setPhone={setPhone} email={email} setEmail={setEmail} colors={colors} />
      <View style={{ marginTop: 12 }}>
        <Btn label={loading ? 'Searching…' : 'Search'} icon="search" kind="primary" size="sm" disabled={!canSearch} onPress={search} />
      </View>

      {decisions && decisions.length === 0 && (
        <Text style={{ color: colors.ink4, fontSize: 12, marginTop: 12 }}>No decisions recorded for this recipient.</Text>
      )}
      {decisions && decisions.length > 0 && (
        <View style={{ marginTop: 14 }}>
          {decisions.map((d) => (
            <View key={d.sendDecisionId} style={[st.decisionRow, { borderColor: colors.line }]}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <Text style={{ color: decisionColor[d.decision] ?? colors.ink3, fontSize: 12, fontWeight: '700', textTransform: 'uppercase' }}>{d.decision}</Text>
                <Text style={{ color: colors.ink3, fontSize: 11 }}>{d.resolvedChannel} · {d.resolvedJurisdiction} · {d.messageClassCode}</Text>
                <Text style={{ color: colors.ink4, fontSize: 11, marginStart: 'auto' }}>{relTime(d.decidedAt)}</Text>
              </View>
              {!!d.refusalReason && <Text style={{ color: colors.ink2, fontSize: 12 }}>{d.refusalReason}</Text>}
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
                <GateFlag label="Gate 1" ok={d.gate1Approval} colors={colors} />
                <GateFlag label="Gate 2" ok={d.gate2Permission} colors={colors} />
                <GateFlag label="Consent" ok={d.consentOk} colors={colors} />
                <GateFlag label="DND" ok={d.dndOk} colors={colors} />
                <GateFlag label="Quiet hours" ok={d.quietHoursOk} colors={colors} />
                <GateFlag label="Frequency" ok={d.frequencyOk} colors={colors} />
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function GateFlag({ label, ok, colors }: { label: string; ok: boolean | null; colors: any }) {
  const color = ok === null ? colors.ink4 : ok ? '#2D8A5F' : '#C94444';
  const text = ok === null ? '—' : ok ? 'OK' : 'failed';
  return (
    <Text style={{ color, fontSize: 10, fontWeight: '600' }}>{label}: {text}</Text>
  );
}

function DndScrubCard({ colors }: { colors: any }) {
  const toast = useToast();
  const { role } = useSession();
  const canManage = role != null && ADMIN_ROLES.has(role);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState('IN');
  const [registryName, setRegistryName] = useState('NDNC');
  const [isRegistered, setIsRegistered] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!canManage) return null;

  const canSave = (!!phone.trim() || !!email.trim()) && !!registryName.trim() && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await upsertDndScrub({
        recipient: { phone: phone.trim() || undefined, email: email.trim() || undefined },
        jurisdictionCode: jurisdictionCode.trim().toUpperCase(),
        registryName: registryName.trim(),
        isRegistered,
      });
      toast.success('DND scrub result recorded');
      setPhone(''); setEmail('');
    } catch (e) {
      handleApiError(e, 'Could not record scrub result');
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={[st.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
      <SectionLabel>Do-not-call registry (admin)</SectionLabel>
      <Banner tone="info" icon="info">
        <Text style={{ color: colors.ink, fontSize: 12 }}>
          There is no live connection to India's NDNC or the UAE's TDRA here — that needs a certified telecom
          integration this service doesn't have credentials for. This form is the manual/ops-fed seam until one exists.
        </Text>
      </Banner>
      <View style={{ marginTop: 12 }}>
        <RecipientFields phone={phone} setPhone={setPhone} email={email} setEmail={setEmail} colors={colors} />
      </View>
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', marginTop: 12, alignItems: 'flex-end' }}>
        <View style={{ width: 100 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Jurisdiction</Text>
          <TextInput value={jurisdictionCode} onChangeText={setJurisdictionCode} placeholder="IN" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
        </View>
        <View style={{ width: 140 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Registry</Text>
          <TextInput value={registryName} onChangeText={setRegistryName} placeholder="NDNC" placeholderTextColor={colors.ink4} style={[st.input, { color: colors.ink, borderColor: colors.line, backgroundColor: colors.paper }]} />
        </View>
        <View style={{ width: 150 }}>
          <Text style={[st.label, { color: colors.ink3 }]}>Status</Text>
          <Select
            value={isRegistered ? 'registered' : 'clear'}
            onChange={(v) => setIsRegistered(v === 'registered')}
            options={[{ value: 'registered', label: 'Registered (blocked)' }, { value: 'clear', label: 'Clear' }]}
          />
        </View>
        <Btn label={saving ? 'Saving…' : 'Record'} kind="primary" size="sm" disabled={!canSave} onPress={save} />
      </View>
    </View>
  );
}

const st = StyleSheet.create({
  card: { padding: 16, borderRadius: 2, borderWidth: 1 },
  label: { fontSize: 11, fontWeight: '600', marginBottom: 5 },
  input: { height: 36, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, fontSize: 13 },
  tr: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, gap: 10 },
  decisionRow: { padding: 10, borderWidth: 1, borderRadius: 2, marginBottom: 8 },
});
